import { useState, useCallback, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Info, BarChart3, Zap, Megaphone, ShieldCheck, Calendar } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PERIOD_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

const QUICK_RANGES = [
  { label: 'This Month', getValue: () => { const now = new Date(); return { start: `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`, end: now.toISOString().slice(0,10) }; }},
  { label: 'Last Month', getValue: () => { const now = new Date(); const lm = new Date(now.getFullYear(), now.getMonth()-1, 1); const le = new Date(now.getFullYear(), now.getMonth(), 0); return { start: lm.toISOString().slice(0,10), end: le.toISOString().slice(0,10) }; }},
  { label: 'This Quarter', getValue: () => { const now = new Date(); const q = Math.floor(now.getMonth()/3); const qs = new Date(now.getFullYear(), q*3, 1); return { start: qs.toISOString().slice(0,10), end: now.toISOString().slice(0,10) }; }},
  { label: 'YTD', getValue: () => { const now = new Date(); return { start: `${now.getFullYear()}-01-01`, end: now.toISOString().slice(0,10) }; }},
];

const fmt = (n) => { if (n == null) return '0'; return Number(n).toLocaleString('en-IN'); };
const fmtK = (n) => { if (n >= 100000) return `${(n/100000).toFixed(1)}L`; if (n >= 1000) return `${(n/1000).toFixed(1)}K`; return fmt(n); };
const costColor = (pct, sales) => { if (!sales) return '#9CA3AF'; if (pct < 25) return '#2D6A4F'; if (pct < 30) return '#D4A017'; return '#C45A41'; };

function FlagBadge({ flag }) {
  const colors = { warning: 'bg-[#C45A41]/10 text-[#C45A41] border-[#C45A41]/20', positive: 'bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/20', info: 'bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/20' };
  const icons = { warning: AlertTriangle, positive: CheckCircle2, info: Info };
  const Icon = icons[flag.type] || Info;
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${colors[flag.type] || colors.info}`} data-testid={`insight-flag-${flag.type}`}>
      <Icon className="w-3 h-3" /> {flag.text}
    </div>
  );
}

function KPICard({ label, value, sub, trend, icon: Icon, color = 'var(--color-primary)', alert: alertMsg }) {
  return (
    <Card className="border-[#E2E8DF] shadow-sm" data-testid={`kpi-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-[#5C6B64] font-medium">{label}</p>
            <p className="text-2xl font-bold mt-1" style={{color}}>{value}</p>
            {sub && <p className="text-xs text-[#5C6B64] mt-0.5">{sub}</p>}
            {alertMsg && (
              <p className="text-[10px] text-[#C45A41] font-medium mt-1 flex items-center gap-1" data-testid={`kpi-alert-${label.toLowerCase().replace(/\s/g,'-')}`}>
                <AlertTriangle className="w-3 h-3 shrink-0" />{alertMsg}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1">
            {Icon && <Icon className="w-4 h-4 text-[#5C6B64]/40" />}
            {trend !== undefined && trend !== null && (
              <span className={`text-xs font-semibold flex items-center gap-0.5 ${trend >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
                {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {trend >= 0 ? '+' : ''}{trend}%
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const chartTooltipStyle = { contentStyle: { background: '#1A2421', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, padding: '8px 12px' }, itemStyle: { color: '#fff' } };
const shortPeriod = (p) => { if (!p) return ''; if (p.length === 10) return p.slice(5); return p; };

export default function ManagerInsights() {
  const { getHeaders } = useAuth();

  const today = new Date().toISOString().slice(0, 10);
  const monthStart = `${today.slice(0,4)}-${today.slice(5,7)}-01`;

  const [startDate, setStartDate] = useState(monthStart);
  const [endDate, setEndDate] = useState(today);
  const [period, setPeriod] = useState('daily');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/insights/manager?start_date=${startDate}&end_date=${endDate}&period=${period}`, { headers: getHeaders() });
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [startDate, endDate, period, getHeaders]);

  useEffect(() => { loadData(); }, [loadData]);

  const s = data?.summary || {};
  const mkt = data?.marketing || {};
  const energy = data?.energy || {};
  const cl = data?.checklist || {};

  const mktBreakdown = useMemo(() => {
    if (!mkt.breakdown) return [];
    return Object.entries(mkt.breakdown).filter(([,v]) => v > 0).map(([k,v]) => ({ name: k.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase()), value: v }));
  }, [mkt.breakdown]);

  return (
      <div className="w-full space-y-3" data-testid="manager-insights">
        {/* Header + Filters */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-[#1A2421]">Insights</h1>
            <p className="text-xs text-[#5C6B64]">{data?.outlet_name || 'Loading...'} — Performance Review</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {QUICK_RANGES.map(qr => (
              <Button key={qr.label} variant="outline" size="sm" className="h-7 text-[10px] border-[#E2E8DF] text-[#5C6B64]"
                onClick={() => { const r = qr.getValue(); setStartDate(r.start); setEndDate(r.end); }}
                data-testid={`quick-range-${qr.label.toLowerCase().replace(/\s/g,'-')}`}>{qr.label}</Button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5 text-[#5C6B64]" />
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-8 px-2 text-xs border border-[#E2E8DF] rounded-md bg-white" data-testid="insights-start-date" />
            <span className="text-xs text-[#5C6B64]">to</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-8 px-2 text-xs border border-[#E2E8DF] rounded-md bg-white" data-testid="insights-end-date" />
          </div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="h-8 w-28 text-xs border-[#E2E8DF]" data-testid="insights-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {loading && <span className="text-xs text-[#5C6B64] animate-pulse">Loading...</span>}
        </div>

        {/* Insight Flags */}
        {data?.flags?.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="insight-flags">
            {data.flags.map((f, i) => <FlagBadge key={i} flag={f} />)}
          </div>
        )}

        {/* ═══ SECTION 1: SUMMARY KPIs ═══ */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="summary-kpis">
          <KPICard label="Net Sales" value={`${fmtK(s.sales_actual)}`} sub={`Bgt ${fmtK(s.sales_budget)}`} trend={s.vs_ly_pct} icon={BarChart3} />
          <KPICard label="Food Cost %" value={s.food_consumption == null ? 'Pending' : (s.food_sale || 0) > 0 ? `${s.fc_pct}%` : 'N/A'} sub={(s.food_sale || 0) > 0 && s.food_consumption != null ? <span title={`₹${fmt(s.food_consumption)} / ₹${fmt(s.food_sale)}`}>on ₹{fmt(s.food_sale)} Food Sales</span> : null} icon={TrendingDown} color={s.food_consumption == null ? '#9CA3AF' : costColor(s.fc_pct, s.food_sale)} alert={s.fc_pct > 100 ? 'Consumption exceeds sales — check entries' : null} />
          <KPICard label="Bev Cost %" value={s.bev_consumption == null ? 'Pending' : (s.bev_sale || 0) > 0 ? `${s.bc_pct}%` : 'N/A'} sub={(s.bev_sale || 0) > 0 && s.bev_consumption != null ? <span title={`₹${fmt(s.bev_consumption)} / ₹${fmt(s.bev_sale)}`}>on ₹{fmt(s.bev_sale)} Bev Sales</span> : null} icon={TrendingDown} color={s.bev_consumption == null ? '#9CA3AF' : costColor(s.bc_pct, s.bev_sale)} alert={s.bc_pct > 100 ? 'Consumption exceeds sales — check entries' : null} />
          <KPICard label="NPS" value={s.nps || 0} icon={CheckCircle2} color={s.nps >= 8 ? '#2D6A4F' : s.nps >= 5 ? '#D4A017' : '#C45A41'} />
          <KPICard label="Compliance" value={`${s.checklist_compliance || 0}%`} icon={ShieldCheck} color={s.checklist_compliance >= 80 ? '#2D6A4F' : '#C45A41'} />
        </div>

        {/* Inventory Warnings */}
        {data?.inventory_warnings?.length > 0 && (
          <Card className="border-[#C45A41]/30 bg-[#C45A41]/5 shadow-sm" data-testid="inventory-warnings">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-[#C45A41]" />
                <span className="text-sm font-semibold text-[#C45A41]">Stock Mismatch — Opening not matching last closing</span>
                <Badge variant="outline" className="text-[10px] border-[#C45A41]/30 text-[#C45A41] ml-auto">{data.inventory_warnings.length} issue{data.inventory_warnings.length > 1 ? 's' : ''}</Badge>
              </div>
              <div className="space-y-1.5">
                {data.inventory_warnings.map((w, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs bg-white/60 rounded px-3 py-2 border border-[#C45A41]/10" data-testid={`inv-warning-${i}`}>
                    <span className="text-[#C45A41] font-medium min-w-[70px]">{w.date}</span>
                    <span className="capitalize text-[#5C6B64] font-medium min-w-[50px]">{w.type}</span>
                    <span className="text-[#5C6B64]">Expected: <strong className="text-[#1A2421]">₹{fmt(w.expected)}</strong></span>
                    <span className="text-[#5C6B64]">Actual: <strong className="text-[#C45A41]">₹{fmt(w.actual)}</strong></span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ═══ SECTION 2: TRENDS ═══ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Sales Comparison Chart */}
          <Card className="border-[#E2E8DF] shadow-sm" data-testid="sales-trend-chart">
            <CardHeader className="pb-1 pt-3 px-4">
              <CardTitle className="text-sm text-[#1A2421]">Sales Comparison</CardTitle>
            </CardHeader>
            <CardContent className="px-2 pb-3">
              {data?.sales_trend?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.sales_trend} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8DF" />
                    <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#5C6B64' }} tickFormatter={shortPeriod} />
                    <YAxis tick={{ fontSize: 10, fill: '#5C6B64' }} tickFormatter={fmtK} />
                    <Tooltip {...chartTooltipStyle} formatter={(v) => fmt(v)} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar dataKey="actual" name="Actual" fill="#2D6A4F" radius={[3,3,0,0]} />
                    <Bar dataKey="budget" name="Budget" fill="#E2E8DF" radius={[3,3,0,0]} />
                    <Bar dataKey="last_year" name="LY" fill="#D4A017" radius={[3,3,0,0]} opacity={0.6} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-[#5C6B64] text-center py-10">No sales data for this period</p>}
            </CardContent>
          </Card>

          {/* NPS Trend */}
          <Card className="border-[#E2E8DF] shadow-sm" data-testid="nps-trend-chart">
            <CardHeader className="pb-1 pt-3 px-4">
              <CardTitle className="text-sm text-[#1A2421]">NPS Trend</CardTitle>
            </CardHeader>
            <CardContent className="px-2 pb-3">
              {data?.nps_trend?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={data.nps_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8DF" />
                    <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#5C6B64' }} tickFormatter={shortPeriod} />
                    <YAxis domain={[0, 10]} tick={{ fontSize: 10, fill: '#5C6B64' }} />
                    <Tooltip {...chartTooltipStyle} />
                    <Area type="monotone" dataKey="nps" stroke="#2D6A4F" fill="#2D6A4F" fillOpacity={0.1} strokeWidth={2} dot={{ r: 3, fill: '#2D6A4F' }} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-[#5C6B64] text-center py-10">No NPS data for this period</p>}
            </CardContent>
          </Card>
        </div>

        {/* ═══ SECTION 3: DRIVERS ═══ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Marketing Spend */}
          <Card className="border-[#E2E8DF] shadow-sm" data-testid="marketing-spend-card">
            <CardHeader className="pb-1 pt-3 px-4">
              <CardTitle className="text-sm text-[#1A2421] flex items-center gap-1.5"><Megaphone className="w-3.5 h-3.5 text-[#5C6B64]" /> Marketing</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-xl font-bold text-[#1A2421]">{fmtK(mkt.total_spend)}</span>
                {mkt.budget > 0 && <span className="text-xs text-[#5C6B64]">/ {fmtK(mkt.budget)} budget ({mkt.utilization_pct}%)</span>}
              </div>
              {mktBreakdown.length > 0 && (
                <div className="space-y-1">
                  {mktBreakdown.map(item => (
                    <div key={item.name} className="flex justify-between text-xs">
                      <span className="text-[#5C6B64]">{item.name}</span>
                      <span className="text-[#1A2421] font-medium">{fmt(item.value)}</span>
                    </div>
                  ))}
                </div>
              )}
              {mktBreakdown.length === 0 && <p className="text-xs text-[#5C6B64]">No marketing data</p>}
            </CardContent>
          </Card>

          {/* Energy / Maintenance */}
          <Card className={`border-[#E2E8DF] shadow-sm ${energy.maintenance_budget > 0 && energy.total_maintenance_spend > energy.maintenance_budget ? 'border-[#C45A41]/30' : ''}`} data-testid="energy-card">
            <CardHeader className="pb-1 pt-3 px-4">
              <CardTitle className="text-sm text-[#1A2421] flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-[#5C6B64]" /> Energy & Maintenance</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <div className="mb-2">
                <span className="text-xl font-bold text-[#1A2421]">{fmtK(energy.total_maintenance_spend || energy.total_cost || 0)}</span>
                <span className="text-xs text-[#5C6B64] ml-1">total spend</span>
                {energy.maintenance_budget > 0 && (
                  <span className={`text-xs ml-2 font-medium ${energy.total_maintenance_spend > energy.maintenance_budget ? 'text-[#C45A41]' : 'text-[#2D6A4F]'}`}>
                    / {fmtK(energy.maintenance_budget)} budget
                  </span>
                )}
              </div>
              {energy.maintenance_budget > 0 && energy.total_maintenance_spend > energy.maintenance_budget && (
                <div className="flex items-center gap-1.5 text-xs text-[#C45A41] font-medium mb-2 px-2 py-1 bg-[#C45A41]/5 rounded" data-testid="maintenance-budget-exceeded">
                  <AlertTriangle className="w-3 h-3" />
                  Budget exceeded by {fmtK(energy.total_maintenance_spend - energy.maintenance_budget)}
                </div>
              )}
              <div className="space-y-1.5">
                {['electricity', 'gas', 'water'].map(mt => {
                  const m = energy[mt] || {};
                  return (
                    <div key={mt} className="flex justify-between text-xs">
                      <span className="text-[#5C6B64] capitalize">{mt}</span>
                      <span className="text-[#1A2421]">{fmt(m.consumption)} {m.unit} &middot; {fmt(m.cost)}</span>
                    </div>
                  );
                })}
                {(energy.expenses_total || 0) > 0 && (
                  <div className="flex justify-between text-xs pt-1 border-t border-[#E2E8DF]">
                    <span className="text-[#5C6B64]">Maint. Expenses</span>
                    <span className="text-[#1A2421] font-medium">{fmt(energy.expenses_total)}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Checklist Compliance */}
          <Card className="border-[#E2E8DF] shadow-sm" data-testid="checklist-card">
            <CardHeader className="pb-1 pt-3 px-4">
              <CardTitle className="text-sm text-[#1A2421] flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#5C6B64]" /> Checklist</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <div className="flex items-baseline gap-2 mb-2">
                <span className={`text-xl font-bold ${(cl.avg_adherence || 0) >= 80 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>{cl.avg_adherence || 0}%</span>
                <span className="text-xs text-[#5C6B64]">avg adherence</span>
              </div>
              <div className="flex gap-4 text-xs mb-2">
                <span className="text-[#5C6B64]">{cl.total_days || 0} days reported</span>
                <span className={`font-medium ${(cl.issues_flagged || 0) > 0 ? 'text-[#C45A41]' : 'text-[#2D6A4F]'}`}>{cl.issues_flagged || 0} issues</span>
              </div>
              {cl.trend?.length > 0 && (
                <ResponsiveContainer width="100%" height={80}>
                  <LineChart data={cl.trend}>
                    <XAxis dataKey="period" hide />
                    <YAxis domain={[0, 100]} hide />
                    <Tooltip {...chartTooltipStyle} formatter={(v) => `${v}%`} />
                    <Line type="monotone" dataKey="adherence_pct" stroke="#2D6A4F" strokeWidth={2} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
  );
}
